import React, { useEffect, useState } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import Accueil from "./pages/Accueil";
import Connexion from "./pages/Connexion";
import Creer_compte from "./pages/Creer_compte";
import Header from "./components/Header";
import Footer from "./components/Footer";
import ConditionsUtilisationPage from "./pages/ConditionsUtilisationPage";
import PolitiqueConfidentialitePage from "./pages/PolitiqueConfidentialitePage";
import AdminDashboard from "./pages/Admin";
import "./i18n";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

function App() {
    const location = useLocation();
    const isAdminLoggedIn = localStorage.getItem("adminToken");
    // Détermine la section actuelle basée sur le hash ou le chemin pour les métadonnées SEO
    const [currentSection, setCurrentSection] = useState("accueil");
    const { t } = useTranslation("seo"); // Hook de traduction pour les textes SEO

    useEffect(() => {
        // Met à jour la section actuelle lorsque le chemin ou le hash de l'URL change
        const hash = location.hash.substring(1);
        setCurrentSection(hash || "accueil");
    }, [location.pathname, location.hash]);

    // Fonction pour obtenir le titre de la page optimisé pour le SEO
    const getTitle = () => {
        switch (currentSection) {
            case "conditionsUtilisation":
                // Titre pour la page des conditions d'utilisation
                return "Conditions d'Utilisation | Mamdjuí Cuisine & Événements";
            case "politiqueConfidentialite":
                // Titre pour la page de politique de confidentialité
                return "Politique de Confidentialité | Mamdjuí Cuisine & Événements";
            case "Apropos":
                // Titre optimisé pour la section "À Propos"
                return (
                    t("about_title") ||
                    "À Propos de Mamdjuí Cuisine | Traiteur Gastronomique Gatineau"
                );
            case "Service":
                // Titre optimisé pour la section "Services"
                return (
                    t("services_title") ||
                    "Nos Services de Traiteur Événementiel | Mamdjuí Cuisine & Événements"
                );
            case "Temoignage":
                // Titre optimisé pour la section "Témoignages"
                return (
                    t("testimonials_title") ||
                    "Avis Clients Mamdjuí Cuisine | Traiteur et Événements Réussis"
                );
            case "Contact":
                // Titre optimisé pour la section "Contact"
                return (
                    t("contact_title") ||
                    "Contactez Mamdjuí Cuisine & Événements à Gatineau"
                );
            case "Creer_compte":
                // Titre optimisé pour la page de création de compte
                return "Créer un Compte Client | Mamdjuí Cuisine & Événements";
            case "Connexion":
                // Titre optimisé pour la page de connexion
                return "Connexion | Mamdjuí Cuisine & Événements";
            case "AdminDashboard":
                // Titre pour le tableau de bord administrateur (peut être plus générique)
                return "Tableau de Bord Administrateur | Mamdjuí Cuisine & Événements";
            default:
                // Titre par défaut pour la page d'accueil (URL "/")
                return (
                    t("home_title") ||
                    "Mamdjuí Cuisine & Événements | Traiteur Africain & Événements Gatineau"
                );
        }
    };

    // Fonction pour obtenir la méta-description de la page optimisée pour le SEO
    const getDescription = () => {
        switch (currentSection) {
            case "conditionsUtilisation":
                // Description pour la page des conditions d'utilisation
                return "Consultez les conditions générales d'utilisation des services de traiteur et événementiels de Mamdjuí Cuisine & Événements.";
            case "politiqueConfidentialite":
                // Description pour la page de politique de confidentialité
                return "Découvrez la politique de confidentialité de Mamdjuí Cuisine & Événements concernant la collecte et l'utilisation de vos données personnelles.";
            case "Apropos":
                // Description optimisée pour la section "À Propos"
                return (
                    t("about_description") ||
                    "Découvrez l'histoire, la passion pour la cuisine africaine et les valeurs de Mamdjuí Cuisine & Événements, votre traiteur de confiance à Gatineau et Ottawa."
                );
            case "Service":
                // Description optimisée pour la section "Services"
                return (
                    t("services_description") ||
                    "Explorez nos services de traiteur événementiel, de planification d'événements et nos créations culinaires uniques pour mariages, anniversaires et réceptions à Gatineau et Ottawa."
                );
            case "Temoignage":
                // Description optimisée pour la section "Témoignages"
                return (
                    t("testimonials_description") ||
                    "Lisez les expériences de nos clients satisfaits qui ont fait confiance à Mamdjuí Cuisine & Événements pour la réussite de leurs mariages, anniversaires et événements d'entreprise."
                );
            case "Contact":
                // Description optimisée pour la section "Contact"
                return (
                    t("contact_description") ||
                    "Contactez l'équipe de Mamdjuí Cuisine & Événements à Gatineau pour discuter de votre prochain événement, demander un devis traiteur personnalisé ou poser vos questions."
                );
            case "Creer_compte":
                // Description optimisée pour la page de création de compte
                return "Créez votre compte client Mamdjuí Cuisine & Événements pour accéder à des offres exclusives, suivre vos devis et simplifier vos commandes en ligne.";
            case "Connexion":
                // Description optimisée pour la page de connexion
                return "Connectez-vous à votre compte Mamdjuí Cuisine & Événements pour gérer vos réservations et accéder à votre tableau de bord client.";
            case "AdminDashboard":
                // Description pour le tableau de bord administrateur
                return "Accédez au tableau de bord administrateur de Mamdjuí Cuisine & Événements pour gérer les réservations et le contenu du site.";
            default:
                // Description par défaut pour la page d'accueil (URL "/")
                return (
                    t("home_description") ||
                    "Bienvenue chez Mamdjuí Cuisine & Événements, votre traiteur de référence pour la cuisine africaine et les services événementiels d'exception à Gatineau et Ottawa."
                );
        }
    };

    return (
        <>
            {/* Utilise Helmet pour gérer les balises <title> et <meta name="description"> */}
            <Helmet>
                <title>{getTitle()}</title>
                <meta name="description" content={getDescription()} />
            </Helmet>
            {/* Affiche l'en-tête si l'utilisateur n'est pas sur le tableau de bord admin et n'est pas connecté en tant qu'admin */}
            {location.pathname !== "/AdminDashboard" && !isAdminLoggedIn && (
                <Header />
            )}
            {/* Définition des routes de l'application */}
            <Routes>
                <Route path="/" element={<Accueil />} />
                <Route path="/Connexion" element={<Connexion />} />
                <Route path="/Creer_compte" element={<Creer_compte />} />
                <Route path="/AdminDashboard" element={<AdminDashboard />} />
                <Route
                    path="/conditionsUtilisation"
                    element={<ConditionsUtilisationPage />}
                />
                <Route
                    path="/politiqueConfidentialite"
                    element={<PolitiqueConfidentialitePage />}
                />
            </Routes>
            {/* Affiche le pied de page si l'utilisateur n'est pas sur le tableau de bord admin et n'est pas connecté en tant qu'admin */}
            {location.pathname !== "/AdminDashboard" && !isAdminLoggedIn && (
                <Footer />
            )}
        </>
    );
}

export default App;
